import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/i18n";

function DefaultEmpty() {
  const { t } = useTranslation("errors");
  return <EmptyState title={t("emptyTitle", "Nothing to show yet.")} />;
}

/** Grid of pulsing card skeletons — for auction-style lists. */
export function CardGridSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div className="grid gap-5 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-xl bg-card border border-border/60 h-[360px] animate-pulse"
          aria-hidden
        />
      ))}
    </div>
  );
}

/** Stack of pulsing row skeletons — for list/table screens. */
export function RowListSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="space-y-3" role="status" aria-label="Loading">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="h-20 rounded-xl border border-border/60 bg-card animate-pulse"
          aria-hidden
        />
      ))}
      <span className="sr-only">Loading…</span>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-dashed border-border/60 p-10 text-center">
      <p className="text-sm font-medium text-foreground">{title}</p>
      {description && (
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      )}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

/** Generic recoverable-error card. Never surfaces raw error details to users. */
export function ErrorState({
  title,
  description,
  onRetry,
  retrying,
}: {
  title?: string;
  description?: string;
  onRetry?: () => void;
  retrying?: boolean;
}) {
  const { t } = useTranslation("errors");
  return (
    <div
      role="alert"
      className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-center"
    >
      <p className="text-sm font-medium text-foreground">{title ?? t("genericTitle", "Something went wrong")}</p>
      <p className="mt-1 text-sm text-muted-foreground">{description ?? t("loadFailed", "We couldn't load this right now.")}</p>
      {onRetry && (
        <div className="mt-4">
          <Button variant="outline" size="sm" onClick={onRetry} disabled={retrying}>
            {retrying ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("retrying", "Retrying…")}
              </>
            ) : (
              t("tryAgain", "Try again")
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Unified switch for a data-loading UI. Renders skeleton / error / empty
 * automatically and the children function when data is ready.
 */
export function DataState<T>({
  isLoading,
  isError,
  onRetry,
  retrying,
  data,
  isEmpty,
  skeleton,
  empty,
  errorTitle,
  errorDescription,
  children,
}: {
  isLoading: boolean;
  isError?: boolean;
  onRetry?: () => void;
  retrying?: boolean;
  data: T | undefined;
  isEmpty?: (data: T) => boolean;
  skeleton?: ReactNode;
  empty?: ReactNode;
  errorTitle?: string;
  errorDescription?: string;
  children: (data: T) => ReactNode;
}) {
  if (isLoading) return <>{skeleton ?? <RowListSkeleton />}</>;
  if (isError) {
    return (
      <ErrorState
        title={errorTitle}
        description={errorDescription}
        onRetry={onRetry}
        retrying={retrying}
      />
    );
  }
  if (data === undefined) return null;
  if (isEmpty && isEmpty(data)) {
    return <>{empty ?? <DefaultEmpty />}</>;
  }
  return <>{children(data)}</>;
}
